import React, { useCallback, useRef, useState } from "react";
import {
  type DealRangeBounds,
  type DealRangeFilters,
  hasActiveRangeFilters,
} from "~/lib/deal-filters";
import { cn, formatNumber } from "~/lib/utils";
import { useT } from "~/lib/i18n";

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function snap(v: number, step: number, min: number): number {
  return Math.round((v - min) / step) * step + min;
}

interface InlineSliderProps {
  label: string;
  min: number;
  max: number;
  valueMin: number;
  valueMax: number;
  onChange: (min: number, max: number) => void;
  formatVal: (v: number) => string;
  step: number;
}

function InlineSlider({
  label,
  min,
  max,
  valueMin,
  valueMax,
  onChange,
  formatVal,
  step,
}: InlineSliderProps): React.JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<"min" | "max" | null>(null);

  const range = max - min;
  const leftPct = range > 0 ? ((valueMin - min) / range) * 100 : 0;
  const rightPct = range > 0 ? ((max - valueMax) / range) * 100 : 0;
  const isActive = valueMin > min || valueMax < max;

  const getValueFromPointer = useCallback(
    (clientX: number): number => {
      const track = trackRef.current;
      if (!track || range <= 0) return min;
      const rect = track.getBoundingClientRect();
      const pct = clamp((clientX - rect.left) / rect.width, 0, 1);
      return snap(min + pct * range, step, min);
    },
    [min, range, step],
  );

  const handlePointerDown = useCallback(
    (thumb: "min" | "max") => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setDragging(thumb);
    },
    [],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      const raw = getValueFromPointer(e.clientX);
      if (dragging === "min") {
        onChange(clamp(raw, min, valueMax - step), valueMax);
      } else {
        onChange(valueMin, clamp(raw, valueMin + step, max));
      }
    },
    [dragging, getValueFromPointer, min, max, valueMin, valueMax, step, onChange],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      setDragging(null);
    },
    [],
  );

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (dragging) return;
      const raw = getValueFromPointer(e.clientX);
      const distToMin = Math.abs(raw - valueMin);
      const distToMax = Math.abs(raw - valueMax);
      if (distToMin <= distToMax) {
        onChange(clamp(raw, min, valueMax - step), valueMax);
      } else {
        onChange(valueMin, clamp(raw, valueMin + step, max));
      }
    },
    [dragging, getValueFromPointer, valueMin, valueMax, min, max, step, onChange],
  );

  if (range <= 0) return <div />;

  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <span className="shrink-0 text-[10px] uppercase tracking-wide text-ink-tertiary mono w-[32px]">
        {label}
      </span>
      <span className={cn("shrink-0 text-[10px] mono w-[52px] text-right", isActive ? "text-ink-primary" : "text-ink-disabled")}>
        {formatVal(valueMin)}
      </span>
      {/* Track */}
      <div
        ref={trackRef}
        className="relative h-7 flex-1 min-w-[80px] flex items-center cursor-pointer touch-none select-none"
        onClick={handleTrackClick}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className="absolute inset-x-2 h-1 rounded-full bg-[var(--surface-sunken)]" />
        <div
          className="absolute h-1 rounded-full bg-accent/50"
          style={{
            left: `calc(${leftPct}% + 8px * ${1 - leftPct / 100})`,
            right: `calc(${rightPct}% + 8px * ${1 - rightPct / 100})`,
          }}
        />
        {/* Min thumb */}
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full border-2 shadow-sm",
            "bg-white border-accent cursor-grab active:cursor-grabbing",
            dragging === "min" && "ring-3 ring-accent/20 scale-125",
          )}
          style={{ left: `calc(${leftPct}% + 8px * ${1 - leftPct / 100})` }}
          onPointerDown={handlePointerDown("min")}
        />
        {/* Max thumb */}
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full border-2 shadow-sm",
            "bg-white border-accent cursor-grab active:cursor-grabbing",
            dragging === "max" && "ring-3 ring-accent/20 scale-125",
          )}
          style={{ left: `calc(${100 - rightPct}% - 8px * ${1 - rightPct / 100} + 8px * ${rightPct / 100})` }}
          onPointerDown={handlePointerDown("max")}
        />
      </div>
      <span className={cn("shrink-0 text-[10px] mono w-[52px]", isActive ? "text-ink-primary" : "text-ink-disabled")}>
        {formatVal(valueMax)}
      </span>
    </div>
  );
}

export function RangeFiltersBar({
  filters,
  onChange,
  bounds,
}: {
  filters: DealRangeFilters;
  onChange: (f: DealRangeFilters) => void;
  bounds: DealRangeBounds | null;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}): React.JSX.Element {
  const { numberLocale } = useT();
  const active = hasActiveRangeFilters(filters);

  if (!bounds || (bounds.priceMax - bounds.priceMin <= 0 && bounds.kmMax - bounds.kmMin <= 0)) {
    return <div />;
  }

  const priceStep = Math.max(100, Math.round((bounds.priceMax - bounds.priceMin) / 80));
  const kmStep = Math.max(100, Math.round((bounds.kmMax - bounds.kmMin) / 80));

  const priceMin = filters.priceMin ?? bounds.priceMin;
  const priceMax = filters.priceMax ?? bounds.priceMax;
  const kmMin = filters.kmMin ?? bounds.kmMin;
  const kmMax = filters.kmMax ?? bounds.kmMax;

  const formatEur = (v: number) =>
    v >= 1000
      ? `${Math.round(v / 1000)}k€`
      : `${v}€`;

  const formatKm = (v: number) =>
    v >= 1000
      ? `${Math.round(v / 1000)}k`
      : `${v}`;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 flex-1 min-w-[240px]">
        <InlineSlider
          label="Prix"
          min={bounds.priceMin}
          max={bounds.priceMax}
          valueMin={priceMin}
          valueMax={priceMax}
          onChange={(min, max) =>
            onChange({
              ...filters,
              priceMin: min <= bounds.priceMin ? null : min,
              priceMax: max >= bounds.priceMax ? null : max,
            })
          }
          formatVal={formatEur}
          step={priceStep}
        />
      </div>
      {bounds.kmMax - bounds.kmMin > 0 && (
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <InlineSlider
            label="Km"
            min={bounds.kmMin}
            max={bounds.kmMax}
            valueMin={kmMin}
            valueMax={kmMax}
            onChange={(min, max) =>
              onChange({
                ...filters,
                kmMin: min <= bounds.kmMin ? null : min,
                kmMax: max >= bounds.kmMax ? null : max,
              })
            }
            formatVal={formatKm}
            step={kmStep}
          />
        </div>
      )}
      {active && (
        <button
          type="button"
          onClick={() => onChange({ priceMin: null, priceMax: null, kmMin: null, kmMax: null })}
          className="shrink-0 h-7 px-2 rounded-md text-[11px] text-ink-tertiary hover:text-ink-primary hover:bg-[var(--surface-hover)] transition-colors"
          title="Réinitialiser"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
